import { useCallback } from 'react';
import type { HilPresentationReplyEnvelopeWriter } from '@bizfirst/hil-core';
import { useHilContext } from '../context/HilContext';
import { useHilStore } from '../store/hilStore';

export function useHilSubmit(): {
  submit: (writer: HilPresentationReplyEnvelopeWriter) => Promise<void>;
  submitting: boolean;
  error: string | null;
  clearError: () => void;
} {
  const { engine } = useHilContext();
  const submitting = useHilStore((s) => s.submitting);
  const error = useHilStore((s) => s.submitError);
  const setSubmitting = useHilStore((s) => s.setSubmitting);
  const setSubmitError = useHilStore((s) => s.setSubmitError);
  const resolveEvent = useHilStore((s) => s._resolveEvent);

  const submit = useCallback(async (writer: HilPresentationReplyEnvelopeWriter) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await writer.submit();
      if (!result.ok) {
        setSubmitError(result.error ?? 'Submission failed');
        return;
      }
      // Pass CorrelationID alongside EngageSessionID so a resolve that finally arrives after a
      // SignalR revival has already replaced `active` with the NEXT (unanswered) turn cannot
      // mismatch onto it — see the isMatch comment in hilStore.ts's `_resolveEvent`.
      const engageSessionID = writer.getEngageSessionID();
      const correlationID = writer.getCorrelationID();
      engine.resolve(engageSessionID, correlationID);
      resolveEvent(engageSessionID, correlationID);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSubmitting(false);
    }
  }, [engine, setSubmitting, setSubmitError, resolveEvent]);

  const clearError = useCallback(() => setSubmitError(null), [setSubmitError]);

  return { submit, submitting, error, clearError };
}
